import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Fade,
  CircularProgress,
  InputAdornment,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Paper
} from '@mui/material';
import { styled } from '@mui/material/styles';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import RestoreIcon from '@mui/icons-material/Restore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

// Styled components
const FormContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2)
}));

const PasswordStrengthIndicator = styled(Box)(({ theme, strength }) => {
  const getColor = () => {
    switch (strength) {
      case 'weak':
        return theme.palette.error.main;
      case 'medium':
        return theme.palette.warning.main;
      case 'strong':
        return theme.palette.success.main;
      default:
        return theme.palette.grey[300];
    }
  };

  return {
    height: 4,
    width: '100%',
    backgroundColor: theme.palette.grey[200],
    borderRadius: 2,
    position: 'relative',
    marginTop: theme.spacing(1),
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      height: '100%',
      width: strength === 'weak' ? '33%' : strength === 'medium' ? '66%' : strength === 'strong' ? '100%' : '0%',
      backgroundColor: getColor(),
      borderRadius: 2,
      transition: 'width 0.3s ease'
    }
  };
});

const SuccessContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: theme.spacing(3),
  textAlign: 'center'
}));

const PasswordReset = ({ onRequestReset, onResetPassword, loading, error, clearError }) => {
  // Form state
  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState('');
  const [activeStep, setActiveStep] = useState(0);
  const [completed, setCompleted] = useState(false);

  // Email validation
  const isEmailValid = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Password validation
  const getPasswordStrength = (password) => {
    if (!password) return '';
    if (password.length < 6) return 'weak';
    if (password.length < 10) return 'medium';
    return 'strong';
  };

  // Password match validation
  const doPasswordsMatch = () => {
    return newPassword === confirmPassword;
  };

  // Handle email change
  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    if (formError) setFormError('');
    if (error) clearError();
  };

  // Handle verification code change
  const handleVerificationCodeChange = (e) => {
    setVerificationCode(e.target.value);
    if (formError) setFormError('');
    if (error) clearError();
  };

  // Handle new password change
  const handleNewPasswordChange = (e) => {
    setNewPassword(e.target.value);
    if (formError) setFormError('');
    if (error) clearError();
  };

  // Handle confirm password change
  const handleConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value);
    if (formError) setFormError('');
    if (error) clearError();
  };

  // Toggle password visibility
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Handle request reset
  const handleRequestReset = () => {
    if (!email) {
      setFormError('Please enter your email address');
      return;
    }

    if (!isEmailValid(email)) {
      setFormError('Please enter a valid email address');
      return;
    }

    onRequestReset(email).then(() => {
      setActiveStep(1);
      setFormError('');
    }).catch(err => {
      setFormError(err.message || 'Failed to send reset code');
    });
  };

  // Handle reset password
  const handleResetPassword = () => {
    if (!verificationCode) {
      setFormError('Please enter the verification code');
      return;
    }

    if (!newPassword) {
      setFormError('Please enter a new password');
      return;
    }

    if (newPassword.length < 6) {
      setFormError('Password must be at least 6 characters');
      return;
    }

    if (!doPasswordsMatch()) {
      setFormError('Passwords do not match');
      return;
    }

    onResetPassword(email, verificationCode, newPassword).then(() => {
      setCompleted(true);
      setActiveStep(2);
      setFormError('');
    }).catch(err => {
      setFormError(err.message || 'Failed to reset password');
    });
  };

  // Reset password steps
  const steps = [
    {
      label: 'Request Password Reset',
      content: (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter your email address and we'll send you a verification code to reset your password.
          </Typography>
          <TextField
            required
            fullWidth
            id="email"
            label="Email Address"
            name="email"
            autoComplete="email"
            autoFocus
            value={email}
            onChange={handleEmailChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
          <Button
            fullWidth
            variant="contained"
            onClick={handleRequestReset}
            disabled={loading || !email}
            sx={{ mt: 2 }}
          >
            {loading ? <CircularProgress size={24} /> : 'Send Reset Code'}
          </Button>
        </Box>
      )
    },
    {
      label: 'Verify and Reset',
      content: (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter the verification code sent to your email and create a new password.
          </Typography>
          <TextField
            required
            fullWidth
            id="verificationCode"
            label="Verification Code"
            name="verificationCode"
            value={verificationCode}
            onChange={handleVerificationCodeChange}
            sx={{ mb: 2 }}
          />
          <TextField
            required
            fullWidth
            name="newPassword"
            label="New Password"
            type={showPassword ? 'text' : 'password'}
            id="newPassword"
            value={newPassword}
            onChange={handleNewPasswordChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <Button
                    aria-label="toggle password visibility"
                    onClick={togglePasswordVisibility}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </Button>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 1 }}
          />
          
          {/* Password strength indicator */}
          {newPassword && (
            <>
              <PasswordStrengthIndicator strength={getPasswordStrength(newPassword)} />
              <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                Password strength: {getPasswordStrength(newPassword) || 'none'}
              </Typography>
            </>
          )}
          
          <TextField
            required
            fullWidth
            name="confirmPassword"
            label="Confirm Password"
            type={showPassword ? 'text' : 'password'}
            id="confirmPassword"
            value={confirmPassword}
            onChange={handleConfirmPasswordChange}
            error={confirmPassword && !doPasswordsMatch()}
            helperText={confirmPassword && !doPasswordsMatch() ? 'Passwords do not match' : ' '}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />
          <Button
            fullWidth
            variant="contained"
            onClick={handleResetPassword}
            disabled={loading || !verificationCode || !newPassword || !confirmPassword}
            startIcon={loading ? null : <RestoreIcon />}
          >
            {loading ? <CircularProgress size={24} /> : 'Reset Password'}
          </Button>
        </Box>
      )
    },
    {
      label: 'Complete',
      content: (
        <SuccessContainer>
          <CheckCircleIcon color="success" sx={{ fontSize: 60, mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Password Reset Successful
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Your password has been successfully reset. You can now log in with your new password.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            href="/login"
          >
            Go to Login
          </Button>
        </SuccessContainer>
      )
    }
  ];

  return (
    <FormContainer>
      {/* Error message */}
      {(error || formError) && (
        <Fade in={Boolean(error || formError)}>
          <Alert 
            severity="error" 
            sx={{ width: '100%' }}
            onClose={() => formError ? setFormError('') : clearError()}
          >
            {formError || error}
          </Alert>
        </Fade>
      )}
      
      {/* Password reset stepper */}
      <Stepper activeStep={activeStep} orientation="vertical">
        {steps.map((step, index) => (
          <Step key={step.label} completed={completed && index === 2}>
            <StepLabel>{step.label}</StepLabel>
            <StepContent>
              {step.content}
            </StepContent>
          </Step>
        ))}
      </Stepper>
    </FormContainer>
  );
};

export default PasswordReset;
